import ErrorAlert from "@/utility/ErrorAlert";
import http from "@/axios/http-common";
import {
  KepatuhanJatuhSession,
  KepatuhanJatuhObservation,
  KepatuhanJatuhKasusRtl,
  KepatuhanJatuhAlasan,
  KepatuhanJatuhRca,
  SignTier
} from "@/types/KepatuhanJatuh";

// Base URL (http-common already applies /api/v1 from VUE_APP_API_URL)
// Routes mirror rme/routes/api.php -> prefix('kmkp/kepatuhan-jatuh')
const BASE = "/kmkp/kepatuhan-jatuh";

function handleError(err: any) {
  ErrorAlert.showErrorAlert(err);
  return Promise.reject(err);
}

// --- Session CRUD ---

async function list(params: any = {}) {
  return await http.getDataParam(BASE, { params }).catch(handleError);
}

async function show(id: number) {
  return await http.getData(`${BASE}/${id}`).catch(handleError);
}

async function create(data: Partial<KepatuhanJatuhSession>) {
  return await http.postData(BASE, data).catch(handleError);
}

async function update(id: number, data: Partial<KepatuhanJatuhSession>) {
  return await http.putData(`${BASE}/${id}`, data).catch(handleError);
}

async function destroy(id: number) {
  return await http.deleteData(`${BASE}/${id}`).catch(handleError);
}

// --- Instruments ---

async function listInstruments(params: any = {}) {
  return await http
    .getDataParam(`${BASE}/instruments`, { params })
    .catch(handleError);
}

// --- Observations ---

async function addObservation(
  id: number,
  data: Partial<KepatuhanJatuhObservation>
) {
  return await http
    .postData(`${BASE}/${id}/observations`, data)
    .catch(handleError);
}

async function updateObservation(
  id: number,
  obsId: number,
  data: Partial<KepatuhanJatuhObservation>
) {
  return await http
    .putData(`${BASE}/${id}/observations/${obsId}`, data)
    .catch(handleError);
}

async function deleteObservation(id: number, obsId: number) {
  return await http
    .deleteData(`${BASE}/${id}/observations/${obsId}`)
    .catch(handleError);
}

async function classifyRisk(id: number, skor: number, instrumentId: number) {
  return await http
    .postData(`${BASE}/${id}/classify-risk`, {
      skor,
      instrument_id: instrumentId
    })
    .catch(handleError);
}

// --- RTL (Rencana Tindak Lanjut) ---

async function autoPopulateRtl(id: number) {
  return await http
    .postData(`${BASE}/${id}/kasus-rtl/auto-populate`, {})
    .catch(handleError);
}

async function updateRtl(
  id: number,
  rtlId: number,
  data: Partial<KepatuhanJatuhKasusRtl>
) {
  return await http
    .putData(`${BASE}/${id}/kasus-rtl/${rtlId}`, data)
    .catch(handleError);
}

// --- Alasan Ketidakpatuhan ---

async function saveAlasan(id: number, data: KepatuhanJatuhAlasan) {
  return await http.postData(`${BASE}/${id}/alasan`, data).catch(handleError);
}

// --- RCA (Root Cause Analysis) ---

async function saveRca(id: number, items: KepatuhanJatuhRca[]) {
  return await http
    .postData(`${BASE}/${id}/rca`, { items })
    .catch(handleError);
}

// --- 3-Tier Signing & Publish ---

async function sign(
  id: number,
  tier: SignTier,
  password: string,
  tanggalVerifikasi?: string
) {
  return await http
    .postData(`${BASE}/${id}/sign`, {
      tier,
      password,
      tanggal_verifikasi: tanggalVerifikasi
    })
    .catch(handleError);
}

async function publish(id: number) {
  return await http.postData(`${BASE}/${id}/publish`, {}).catch(handleError);
}

async function revoke(id: number) {
  return await http.postData(`${BASE}/${id}/revoke`, {}).catch(handleError);
}

// --- PDF ---

async function printPdf(id: number) {
  const resp: any = await http
    .getDataParam(`${BASE}/${id}/pdf`, { responseType: "blob" })
    .catch(handleError);

  const url = URL.createObjectURL(
    new Blob([resp.data], { type: "application/pdf" })
  );
  window.open(url, "_blank");
  setTimeout(() => URL.revokeObjectURL(url), 60000);
}

// --- Rekap & Dashboard ---

async function rekap(params: any = {}) {
  return await http
    .getDataParam(`${BASE}/rekap`, { params })
    .catch(handleError);
}

async function generateRekap(params: any) {
  return await http
    .postData(`${BASE}/rekap/generate`, params)
    .catch(handleError);
}

async function dashboard(tahun: number) {
  return await http
    .getDataParam(`${BASE}/dashboard`, { params: { tahun } })
    .catch(handleError);
}

export default {
  list,
  show,
  create,
  update,
  destroy,
  listInstruments,
  addObservation,
  updateObservation,
  deleteObservation,
  classifyRisk,
  autoPopulateRtl,
  updateRtl,
  saveAlasan,
  saveRca,
  sign,
  publish,
  revoke,
  printPdf,
  rekap,
  generateRekap,
  dashboard
};
